import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Store, Wallet, CreditCard, Coins, Smartphone, ArrowRightLeft, Shield, Zap, MapPin, Eye, EyeOff } from 'lucide-react';
import AddressAutocomplete from '@/components/AddressAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [userType, setUserType] = useState('merchant');
  const [loading, setLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [detectedProvince, setDetectedProvince] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone_number: '',
    id_number: '',
    business_name: '',
    province: '',
    business_address: ''
  });

  // South African provinces in alphabetical order
  const provinces = [
    'Eastern Cape',
    'Free State',
    'Gauteng',
    'KwaZulu-Natal',
    'Limpopo',
    'Mpumalanga',
    'Northern Cape',
    'North West',
    'Western Cape'
  ];

  // Get ordered provinces list (detected first if available, then alphabetically)
  const getOrderedProvinces = () => {
    if (detectedProvince && provinces.includes(detectedProvince)) {
      // Put detected province first, then others alphabetically
      return [
        detectedProvince,
        ...provinces.filter(p => p !== detectedProvince)
      ];
    }
    return provinces;
  };

  // Detect user's province from location (nice-to-have feature)
  useEffect(() => {
    // Trigger for both merchants and customers
    if (navigator.geolocation && showAuth && authMode === 'register') {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Use reverse geocoding to get province
            if (window.google && window.google.maps) {
              const geocoder = new window.google.maps.Geocoder();
              const latlng = { lat: latitude, lng: longitude };
              
              geocoder.geocode({ location: latlng }, (results, status) => {
                if (status === 'OK' && results[0]) {
                  // Find province from address components
                  const provinceComponent = results[0].address_components.find(
                    component => component.types.includes('administrative_area_level_1')
                  );
                  
                  if (provinceComponent) {
                    const province = provinceComponent.long_name;
                    // Check if it matches one of our provinces
                    if (provinces.includes(province)) {
                      setDetectedProvince(province);
                      console.log('Detected province:', province);
                    }
                  }
                }
              });
            }
          } catch (error) {
            console.log('Location detection failed:', error);
          }
        },
        (error) => {
          console.log('Geolocation not available or denied:', error);
        },
        { timeout: 5000, maximumAge: 300000 } // 5 min cache
      );
    }
  }, [showAuth, authMode]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const payload = authMode === 'login'
        ? { email: formData.email, password: formData.password, user_type: userType }
        : { ...formData, user_type: userType };

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      if (authMode === 'login') {
        localStorage.setItem('mojo_token', response.data.token);
        localStorage.setItem('mojo_user', JSON.stringify(response.data.user));
        localStorage.setItem('mojo_wallet_id', response.data.wallet_id);
        
        toast.success('Login successful!');
        
        // Check user type and redirect accordingly
        const userType = response.data.user.user_type;
        let redirectPath;
        
        if (userType === 'admin') {
          redirectPath = '/admin';
          // Also store admin token for admin dashboard
          localStorage.setItem('mojo_admin_token', response.data.token);
        } else if (userType === 'merchant') {
          redirectPath = '/merchant';
        } else {
          redirectPath = '/customer';
        }
        
        setTimeout(() => navigate(redirectPath), 500);
      } else {
        // Registration - redirect directly to dashboard
        localStorage.setItem('mojo_token', response.data.token);
        localStorage.setItem('mojo_user', JSON.stringify(response.data.user));
        localStorage.setItem('mojo_wallet_id', response.data.wallet_id);
        
        toast.success('Account created successfully!');
        toast.info('Complete ID verification to unlock all features');
        
        // Redirect directly to dashboard
        const redirectPath = userType === 'merchant' ? '/merchant' : '/customer';
        setTimeout(() => navigate(redirectPath), 500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationComplete = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/biometric/verify`,
        {
          id_number: formData.id_number,
          access_token: verificationToken
        },
        { headers: { Authorization: `Bearer ${tempUserData.token}` } }
      );

      if (response.data.verified && response.data.verification_score >= 80) {
        localStorage.setItem('mojo_user', JSON.stringify(tempUserData.user));
        localStorage.setItem('mojo_wallet_id', tempUserData.wallet_id);
        
        toast.success(`Verification successful! Score: ${response.data.verification_score}%`);
        
        const redirectPath = tempUserData.user.user_type === 'merchant' ? '/merchant' : '/customer';
        setTimeout(() => navigate(redirectPath), 1000);
      } else {
        toast.error(`Verification failed. Score: ${response.data.verification_score}% (minimum 80% required)`);
      }
    } catch (error) {
      toast.error('Verification failed. Please try again.');
    } finally {
      setLoading(false);
      setShowVerification(false);
    }
  };

  const features = [
    {
      icon: <Store className="w-8 h-8" />,
      title: 'Merchant Wallet',
      description: 'Accept payments, manage transactions, and grow your business'
    },
    {
      icon: <Wallet className="w-8 h-8" />,
      title: 'Wallet to Wallet',
      description: 'Instant transfers between Mojo Money wallets'
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: 'Card Payments',
      description: 'Accept card payments with low transaction fees'
    },
    {
      icon: <Coins className="w-8 h-8" />,
      title: 'ACE Token',
      description: 'Trade carbon-backed crypto tokens on Hedera DLT'
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: 'Airtime & Data',
      description: 'Purchase airtime and data bundles instantly'
    },
    {
      icon: <ArrowRightLeft className="w-8 h-8" />,
      title: 'Money Transfer',
      description: 'Send money across Africa with ACE stable coin'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Biometric Security',
      description: 'Advanced biometric authentication via VirtuallVerify'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Instant Settlement',
      description: 'Real-time transaction processing and settlement'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5F0] via-[#F5EDE4] to-[#F5E8D8]">
      {/* Header */}
      <header className="border-b border-[#F2B10C]/20 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/sticker.svg" alt="Mojo Money" className="h-12 w-12" />
            <div>
              <h1 className="text-2xl font-bold text-[#1B0C34]">Mojo Money</h1>
              <p className="text-xs text-[#145539]">Payments & Remittances</p>
            </div>
          </div>
          <Button 
            data-testid="get-started-btn"
            onClick={() => setShowAuth(true)}
            className="bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold px-6 rounded-full"
          >
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center max-w-4xl mx-auto fade-in">
          <h2 className="text-5xl md:text-6xl font-bold text-[#1B0C34] mb-6">
            Africa's Future of
            <span className="block text-[#F2B10C] mt-2">Payments & Remittances</span>
          </h2>
          <p className="text-xl text-[#1B0C34]/70 mb-8 max-w-2xl mx-auto">
            Powered by Hedera DLT and ACE token stable coin. 
            Accept payments, send money, and trade with confidence.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              data-testid="merchant-signup-btn"
              onClick={() => {
                setUserType('merchant');
                setAuthMode('register');
                setShowAuth(true);
              }}
              size="lg"
              className="bg-[#1B0C34] hover:bg-[#2D1854] text-white px-8 rounded-full text-lg"
            >
              Merchant Sign Up
            </Button>
            <Button
              data-testid="customer-signup-btn"
              onClick={() => {
                setUserType('customer');
                setAuthMode('register');
                setShowAuth(true);
              }}
              size="lg"
              variant="outline"
              className="border-[#F2B10C] text-[#1B0C34] hover:bg-[#F2B10C]/10 px-8 rounded-full text-lg"
            >
              Customer Sign Up
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-20">
        <h3 className="text-4xl font-bold text-center text-[#1B0C34] mb-12">Everything You Need</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <Card key={index} className="card-hover bg-white border-[#F2B10C]/20" data-testid={`feature-card-${index}`}>
              <CardHeader>
                <div className="w-16 h-16 rounded-full bg-[#F2B10C]/10 flex items-center justify-center text-[#F2B10C] mb-4">
                  {feature.icon}
                </div>
                <CardTitle className="text-[#1B0C34]">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-[#1B0C34]/70">{feature.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col" data-testid="auth-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-2xl text-[#1B0C34]">Welcome to Mojo Money</DialogTitle>
            <DialogDescription>Sign in or create an account to get started</DialogDescription>
          </DialogHeader>
          <Tabs value={authMode} onValueChange={setAuthMode} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>
            <div className="flex-1 overflow-y-auto px-1">
              <TabsContent value="login">
                <form onSubmit={handleAuth} className="space-y-4 py-4 pb-6">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="login-email-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showLoginPassword ? "text" : "password"}
                      data-testid="login-password-input"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      data-testid="toggle-login-password"
                    >
                      {showLoginPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </TabsContent>
            <TabsContent value="register">
              <form id="register-form" onSubmit={handleAuth} className="space-y-3 py-4 pb-6">
                <div>
                  <Label className="text-sm">Account Type</Label>
                  <div className="flex gap-2 mt-1">
                    <Button
                      type="button"
                      data-testid="select-merchant-btn"
                      variant={userType === 'merchant' ? 'default' : 'outline'}
                      className={userType === 'merchant' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                      onClick={() => setUserType('merchant')}
                    >
                      Merchant
                    </Button>
                    <Button
                      type="button"
                      data-testid="select-customer-btn"
                      variant={userType === 'customer' ? 'default' : 'outline'}
                      className={userType === 'customer' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                      onClick={() => setUserType('customer')}
                    >
                      Customer
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="full_name" className="text-sm">Full Name</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    data-testid="register-fullname-input"
                    className="h-9"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="reg_email" className="text-sm">Email</Label>
                  <Input
                    id="reg_email"
                    name="email"
                    type="email"
                    data-testid="register-email-input"
                    className="h-9"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone_number" className="text-sm">Phone Number</Label>
                  <Input
                    id="phone_number"
                    name="phone_number"
                    data-testid="register-phone-input"
                    placeholder="+27..."
                    className="h-9"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="id_number" className="text-sm">ID Number</Label>
                  <Input
                    id="id_number"
                    name="id_number"
                    data-testid="register-id-input"
                    placeholder="13-digit SA ID"
                    className="h-9"
                    value={formData.id_number}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div>
                  <Label htmlFor="customer_province" className="text-sm">Province</Label>
                  <select
                    id="customer_province"
                    name="province"
                    data-testid="customer-register-province-select"
                    value={formData.province}
                    onChange={handleInputChange}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select Province</option>
                    {getOrderedProvinces().map((province, index) => (
                      <option key={province} value={province}>
                        {province}
                        {index === 0 && detectedProvince === province ? ' (Your Location)' : ''}
                      </option>
                    ))}
                  </select>
                  {detectedProvince && (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ {detectedProvince} detected from your location
                    </p>
                  )}
                </div>
                
                {/* Merchant-specific fields */}
                {userType === 'merchant' && (
                  <>
                    <div>
                      <Label htmlFor="business_name" className="text-sm">Business Name</Label>
                      <Input
                        id="business_name"
                        name="business_name"
                        data-testid="register-business-name-input"
                        placeholder="Your business name"
                        className="h-9"
                        value={formData.business_name}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="province" className="text-sm">Province</Label>
                      <select
                        id="province"
                        name="province"
                        data-testid="register-province-select"
                        value={formData.province}
                        onChange={handleInputChange}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="">Select Province</option>
                        {getOrderedProvinces().map((province, index) => (
                          <option key={province} value={province}>
                            {province}
                            {index === 0 && detectedProvince === province ? ' (Your Location)' : ''}
                          </option>
                        ))}
                      </select>
                      {detectedProvince && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ {detectedProvince} detected from your location
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="business_address" className="text-sm flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        Business Address
                      </Label>
                      <AddressAutocomplete
                        id="business_address"
                        name="business_address"
                        data-testid="register-address-input"
                        placeholder="Start typing your business address..."
                        className="h-9"
                        value={formData.business_address}
                        onChange={handleInputChange}
                      />
                      <p className="text-xs text-[#1B0C34]/60 mt-0.5">
                        Google Maps autocomplete - Search for your business address
                      </p>
                    </div>
                  </>
                )}
                <div>
                  <Label htmlFor="reg_password" className="text-sm">Password</Label>
                  <div className="relative">
                    <Input
                      id="reg_password"
                      name="password"
                      type={showRegisterPassword ? "text" : "password"}
                      data-testid="register-password-input"
                      className="h-9 pr-10"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      data-testid="toggle-register-password"
                    >
                      {showRegisterPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </TabsContent>
            </div>
            {/* Sticky footer with action buttons */}
            <div className="flex-shrink-0 pt-3 border-t bg-white">
              {authMode === 'login' && (
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    const form = document.querySelector('form');
                    if (form) form.requestSubmit();
                  }}
                  data-testid="login-submit-btn-footer"
                  className="w-full bg-[#1B0C34] hover:bg-[#2D1854] text-white h-10"
                  disabled={loading}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              )}
              {authMode === 'register' && (
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    const form = document.getElementById('register-form');
                    if (form) form.requestSubmit();
                  }}
                  data-testid="register-submit-btn-footer"
                  className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] h-10"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              )}
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-[#1B0C34] text-white py-8 mt-20">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm opacity-70">© 2025 Mojo Money. Powered by Hedera DLT & ACE Token.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
